import { Box, Grid } from "@mui/material";
import { isEmpty } from "lodash-es";
import { type FC, useCallback, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { config } from "../../../config";
import type { UUID } from "../../../utils/uuid";
import { useAccountLimitsContext } from "../../context/account-limit-context";
import { CustomEmailHeaderSelect } from "../../lexical-email-moment/custom-email-header-select";
import { EmailSubjectField } from "../../lexical-email-moment/email-subject-field";
import { useFilterTags } from "../../merge-tag/components/use-filter-tags";
import type { MergeTag } from "../../merge-tag/types";
import { mapUnlayerMergeTags } from "../../merge-tag/util";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { useToTarget } from "../../to/use-to-target";
import { EmailEditor } from "../../unlayer-email-content/components/unlayer-editor";
import { EmailAddressSelect } from "./email-address-selector";
import { useIsNewMoment } from "../../moment/component/moment-new-context";

export const EmailMomentForm: FC<{
  disabled?: boolean;
  showValidationErrors: boolean;
  mergeTags: MergeTag[];
  restrictedEmailFrom: UUID[];
}> = ({ disabled, showValidationErrors, mergeTags, restrictedEmailFrom }) => {
  const { enable_advanced_email_headers } = useAccountLimitsContext();
  const isNewMoment = useIsNewMoment();
  const [emailBodyPlainText, setEmailBodyPlainText] = useState("");
  const getMessageBody = useCallback(() => emailBodyPlainText, [emailBodyPlainText]);

  const validationError = (
    fieldError: string | undefined,
    fieldTouched: boolean | undefined,
  ): string | undefined => {
    if (!fieldError) {
      return undefined;
    }

    return showValidationErrors || fieldTouched ? fieldError : undefined;
  };

  const toTarget = useToTarget();

  const methods = useFormContext<MomentFormMoment>();
  const {
    control,
    formState: { touchedFields, errors },
    getValues,
  } = methods;

  useWatch({
    control,
    name: ["email_moment.design", "email_moment.newDesign"],
  });
  const [watchedDesign, watchedNewDesign] = getValues([
    "email_moment.design",
    "email_moment.newDesign",
  ]);

  useWatch({ control, name: "trigger_survey_id" });
  const isSurveyTrigger = !!getValues("trigger_survey_id");

  const filterTags = useFilterTags(
    mergeTags.filter((x) => x.version === 2),
    toTarget,
  );

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="email_moment.from_address"
          control={control}
          render={({ field, fieldState }) => {
            const error = validationError(fieldState.error?.message, fieldState.isTouched);

            return (
              <Box sx={{ m: 1 }}>
                <EmailAddressSelect
                  value={field.value || config.defaultEmailFrom}
                  onChange={field.onChange}
                  disabled={disabled}
                  restrictedEmailFrom={restrictedEmailFrom}
                  helperText={error}
                  hasError={!!error}
                />
              </Box>
            );
          }}
        />
      </Grid>
      <Grid size={{ xs: 12 }} sx={{ pt: 2 }}>
        <Controller
          name="email_moment.subject"
          control={control}
          render={({ field, fieldState }) => {
            const error = validationError(fieldState.error?.message, fieldState.isTouched);

            return (
              <EmailSubjectField
                value={field.value ?? ""}
                onChange={field.onChange}
                filterTags={filterTags}
                getMessageBody={getMessageBody}
                textFieldVariant="standard"
                textFieldSx={{ ml: 1 }}
                error={error}
                disabled={disabled}
              />
            );
          }}
        />
      </Grid>
      {enable_advanced_email_headers && <CustomEmailHeaderSelect disabled={disabled} />}
      <Grid size={{ xs: 4 }} sx={{ pt: 2 }}>
        <EmailEditor
          design={watchedDesign ?? null}
          mergeTags={mapUnlayerMergeTags(mergeTags, toTarget)}
          disabled={disabled}
          onPlainTextChange={setEmailBodyPlainText}
          isSurveyTrigger={isSurveyTrigger}
          insertSurveyBlockOnMount={isNewMoment}
          error={validationError(
            errors.email_moment?.body?.message ||
              errors.email_moment?.design?.message ||
              (watchedNewDesign ? errors.email_moment?.newDesign?.message : ""),
            typeof touchedFields.email_moment !== "boolean" &&
              !isEmpty(touchedFields.email_moment?.design),
          )}
        />
      </Grid>
    </>
  );
};
